import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

interface GoalAnalysis {
  id: number;
  original_goal: string;
  extracted_action: string;
  refocused_goal: string;
  goal_type: string;
  clarity_score: number;
  is_actionable: boolean;
  created_at: string;
  session_id: string;
  analysis_main?: string;
  analysis_strategic?: string;
  analysis_tactical?: string;
  analysis_critical?: string;
  // Feedback fields
  user_rating?: number;
  was_helpful?: boolean;
  feedback_text?: string;
  improvement_notes?: string;
  action_taken?: boolean;
  feedback_timestamp?: string;
}

export const GoalsHistory: React.FC = () => {
  const { token } = useAuth();
  const [goals, setGoals] = useState<GoalAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGoal, setSelectedGoal] = useState<GoalAnalysis | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'timeline' | 'insights'>('list');
  const [filter, setFilter] = useState<'all' | 'actionable' | 'unclear'>('all');

  useEffect(() => {
    fetchGoalHistory();
  }, [token]);

  const fetchGoalHistory = async () => {
    if (!token) return;

    try {
      const response = await fetch(buildApiUrl('/ai/goals/history'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setGoals(data.goals || []);
      }
    } catch (error) {
      console.error('Failed to fetch goal history:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportGoals = () => {
    const exportData = goals.map(goal => ({
      date: new Date(goal.created_at).toLocaleDateString(),
      time: new Date(goal.created_at).toLocaleTimeString(),
      original: goal.original_goal,
      action: goal.extracted_action,
      refocused: goal.refocused_goal,
      type: goal.goal_type,
      clarity: goal.clarity_score,
      actionable: goal.is_actionable,
    }));

    const csv = [
      Object.keys(exportData[0]).join(','),
      ...exportData.map(row => Object.values(row).map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `goalyet-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const filteredGoals = goals.filter(goal => {
    if (filter === 'actionable') return goal.is_actionable;
    if (filter === 'unclear') return goal.clarity_score < 5;
    return true;
  });

  const getInsights = () => {
    const totalGoals = goals.length;
    const actionableGoals = goals.filter(g => g.is_actionable).length;
    const avgClarity = goals.reduce((sum, g) => sum + (g.clarity_score || 0), 0) / totalGoals || 0;
    const goalTypes = goals.reduce((acc, g) => {
      acc[g.goal_type] = (acc[g.goal_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    // Feedback metrics
    const goalsWithFeedback = goals.filter(g => g.user_rating || g.was_helpful !== undefined);
    const avgRating = goalsWithFeedback.reduce((sum, g) => sum + (g.user_rating || 0), 0) / goalsWithFeedback.length || 0;
    const helpfulCount = goals.filter(g => g.was_helpful).length;
    const actionsTaken = goals.filter(g => g.action_taken).length;

    return {
      totalGoals,
      actionableGoals,
      avgClarity,
      goalTypes,
      goalsWithFeedback: goalsWithFeedback.length,
      avgRating,
      helpfulCount,
      actionsTaken
    };
  };

  const insights = getInsights();

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-orange-500 to-teal-500 bg-clip-text text-transparent">
          📊 Goals History & Insights
        </h1>
        <p className="text-gray-600">
          Track every goal analysis and extracted action. Perfect for development insights.
        </p>
      </div>

      {/* Controls */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'list'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📋 List View
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'timeline'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            📅 Timeline
          </button>
          <button
            onClick={() => setViewMode('insights')}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              viewMode === 'insights'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            💡 Insights
          </button>
        </div>

        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-4 py-2 rounded-lg border border-gray-300 focus:border-teal-500"
          >
            <option value="all">All Goals</option>
            <option value="actionable">Actionable Only</option>
            <option value="unclear">Needs Clarity</option>
          </select>

          <button
            onClick={exportGoals}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {/* Insights View */}
      {viewMode === 'insights' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-lg border border-orange-200 p-6">
              <div className="text-3xl font-bold text-orange-600">{insights.totalGoals}</div>
              <div className="text-gray-600">Total Goals Analyzed</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg border border-teal-200 p-6">
              <div className="text-3xl font-bold text-teal-600">
                {insights.totalGoals > 0 ? ((insights.actionableGoals / insights.totalGoals) * 100).toFixed(0) : 0}%
              </div>
              <div className="text-gray-600">Actionable Goals</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg border border-blue-200 p-6">
              <div className="text-3xl font-bold text-blue-600">{insights.avgClarity.toFixed(1)}/10</div>
              <div className="text-gray-600">Average Clarity</div>
            </div>
            <div className="bg-white rounded-lg shadow-lg border border-purple-200 p-6">
              <div className="text-lg font-bold text-purple-600">
                {Object.entries(insights.goalTypes)[0]?.[0] || 'N/A'}
              </div>
              <div className="text-gray-600">Most Common Type</div>
            </div>
          </div>

          {/* Feedback Metrics */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-orange-300 p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4">📊 Feedback Metrics (Development)</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {insights.avgRating > 0 ? `${insights.avgRating.toFixed(1)} ⭐` : 'No ratings'}
                </div>
                <div className="text-sm text-gray-600">Average Rating</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">{insights.helpfulCount}</div>
                <div className="text-sm text-gray-600">Marked Helpful</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-teal-600">{insights.actionsTaken}</div>
                <div className="text-sm text-gray-600">Actions Taken</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {insights.goalsWithFeedback}/{insights.totalGoals}
                </div>
                <div className="text-sm text-gray-600">Have Feedback</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading goal history...</div>
          ) : filteredGoals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No goals analyzed yet</div>
          ) : (
            filteredGoals.map((goal) => (
              <div
                key={goal.id}
                className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 hover:border-teal-400 transition-all cursor-pointer"
                onClick={() => setSelectedGoal(goal)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-2">{goal.original_goal}</h3>
                    <div className="text-sm text-gray-600">
                      <span className="inline-block px-2 py-1 bg-orange-100 text-orange-700 rounded mr-2">
                        {goal.goal_type}
                      </span>
                      <span className="inline-block px-2 py-1 bg-blue-100 text-blue-700 rounded mr-2">
                        Clarity: {goal.clarity_score}/10
                      </span>
                      {goal.is_actionable && (
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded mr-2">
                          ✓ Actionable
                        </span>
                      )}
                      {goal.user_rating && (
                        <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-700 rounded mr-2">
                          {goal.user_rating}⭐
                        </span>
                      )}
                      {goal.action_taken && (
                        <span className="inline-block px-2 py-1 bg-teal-100 text-teal-700 rounded">
                          ✓ Did it!
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(goal.created_at).toLocaleDateString()}
                  </div>
                </div>
                {goal.extracted_action && (
                  <div className="p-3 bg-gradient-to-r from-orange-50 to-teal-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700">🎯 Next Action:</div>
                    <div className="text-gray-900 font-mono text-sm">{goal.extracted_action}</div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div className="relative">
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>
          {filteredGoals.map((goal, index) => (
            <div key={goal.id} className="relative flex items-start mb-8">
              <div className="absolute left-8 w-4 h-4 bg-orange-500 rounded-full -translate-x-1/2"></div>
              <div className="ml-16 flex-1">
                <div className="text-sm text-gray-500 mb-1">
                  {new Date(goal.created_at).toLocaleString()}
                </div>
                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4">
                  <div className="font-bold text-gray-900 mb-2">{goal.original_goal}</div>
                  {goal.extracted_action && (
                    <div className="text-sm text-teal-700 bg-teal-50 p-2 rounded">
                      → {goal.extracted_action}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Goal Detail Modal */}
      {selectedGoal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">Goal Analysis Details</h2>
              <button
                onClick={() => setSelectedGoal(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-gray-700">Original Goal:</h3>
                <p className="text-gray-900">{selectedGoal.original_goal}</p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700">Extracted Action:</h3>
                <p className="text-gray-900 bg-orange-50 p-3 rounded">
                  {selectedGoal.extracted_action || 'No action extracted'}
                </p>
              </div>

              <div>
                <h3 className="font-bold text-gray-700">Refocused Goal:</h3>
                <p className="text-gray-900">{selectedGoal.refocused_goal || 'Not refocused'}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="font-bold text-gray-700">Type:</span> {selectedGoal.goal_type}
                </div>
                <div>
                  <span className="font-bold text-gray-700">Clarity:</span> {selectedGoal.clarity_score}/10
                </div>
                <div>
                  <span className="font-bold text-gray-700">Actionable:</span> {selectedGoal.is_actionable ? 'Yes' : 'No'}
                </div>
                <div>
                  <span className="font-bold text-gray-700">Session:</span> {selectedGoal.session_id}
                </div>
              </div>

              {selectedGoal.analysis_main && (
                <div>
                  <h3 className="font-bold text-gray-700">Full Analysis:</h3>
                  <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto">
                    {selectedGoal.analysis_main}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};